import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Pause, Play, Share2, SkipBack, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { shareCard } from "../lib/shareCard";
import SaveButton from "../components/SaveButton";
import AskWork from "../components/AskWork";
import LiveContext from "../components/LiveContext";
import { useAuth } from "../context/AuthContext";
import Visualizer from "../components/Visualizer";
import { MaskLines, FadeUp } from "../components/Reveal";
import { useAudio } from "../context/AudioContext";
import { api, fmt } from "../lib/api";
import useSeo from "../lib/useSeo";

function Deck() {
  const { tracks, current, playing, time, duration, toggle, step, seek } = useAudio();
  const t = current || tracks[0];
  if (!t) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid="listen-deck">
      <div className="lg:col-span-6"><Visualizer cover={t.cover} /></div>
      <div className="lg:col-span-6">
        <p className="eyebrow mb-4">Now in the room &middot; {t.kind}</p>
        <h2 className="f-display font-black uppercase text-5xl sm:text-7xl leading-[0.86]" data-testid="listen-current-title">{t.title}</h2>
        <p className="f-serif italic text-2xl text-[#cfcec8] mt-3">{t.version}</p>
        <div className="mt-10 flex items-center gap-4 f-mono text-xs text-[var(--ash)]">
          <span className="w-12">{fmt(current ? time : 0)}</span>
          <input type="range" min={0} max={(current ? duration : t.duration) || 1} step={0.1} value={current ? time : 0} onChange={(e) => current && seek(+e.target.value)} className="range-thin" aria-label="Seek" data-testid="listen-seek" />
          <span className="w-12 text-right">{fmt(current ? duration : t.duration)}</span>
        </div>
        <div className="mt-8 flex items-center gap-6">
          <button onClick={() => step(-1)} className="p-3 hover:text-[var(--signal)] transition-colors" aria-label="Previous" data-testid="listen-prev"><SkipBack size={20} /></button>
          <button onClick={() => toggle(t)} className="w-20 h-20 rounded-full border border-[var(--bone)] flex items-center justify-center hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors duration-300" aria-label={playing ? "Pause" : "Play"} data-testid="listen-play-toggle">
            {playing && current ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
          </button>
          <button onClick={() => step(1)} className="p-3 hover:text-[var(--signal)] transition-colors" aria-label="Next" data-testid="listen-next"><SkipForward size={20} /></button>
        </div>
      </div>
    </div>
  );
}

function TrackList() {
  const { tracks, current, playing, toggle } = useAudio();
  const { login } = useAuth();
  const kinds = ["All", ...Array.from(new Set(tracks.map((t) => t.kind)))];
  const [kind, setKind] = useState("All");
  const [limit, setLimit] = useState(24);
  const list = kind === "All" ? tracks : tracks.filter((t) => t.kind === kind);
  return (
    <div>
    <div className="flex flex-wrap items-center gap-2 mb-6" role="tablist" aria-label="Filter audio">
      {kinds.map((k) => (
        <button key={k} role="tab" aria-selected={kind === k} onClick={() => { setKind(k); setLimit(24); }} className={`f-mono text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-colors duration-300 ${kind === k ? "bg-[var(--bone)] text-[var(--ink)] border-[var(--bone)]" : "border-[var(--line)] text-[#cfcec8] hover:border-[var(--bone)]"}`} data-testid={`listen-filter-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{k}</button>
      ))}
      <span className="f-mono text-[11px] text-[var(--ash)] ml-2" data-testid="listen-track-count">{list.length} recordings</span>
    </div>
    <ol className="border-t border-[var(--bone)]" data-testid="listen-tracklist">
      {list.slice(0, limit).map((t, i) => {
        const active = current?.slug === t.slug;
        return (
          <li key={t.slug} className="border-b border-[var(--line)] flex items-center gap-3">
            <button onClick={() => (t.locked ? login() : toggle(t))} className={`group flex-1 grid grid-cols-12 gap-3 py-5 items-center text-left transition-colors ${active ? "text-[var(--bone)]" : "text-[#cfcec8] hover:text-[var(--bone)]"}`} data-testid={`listen-track-${t.slug}`}>
              <span className="col-span-2 sm:col-span-1 f-mono text-xs">
                {active && playing ? <span className="inline-block w-2 h-2 rounded-full bg-[var(--signal)] animate-pulse" /> : String(i + 1).padStart(2, "0")}
              </span>
              <img src={t.cover} alt="" className="hidden sm:block sm:col-span-1 w-12 h-12 object-cover grayscale" />
              <span className="col-span-8 sm:col-span-7">
                <span className="block f-display font-bold uppercase text-2xl leading-none group-hover:translate-x-1 transition-transform duration-500">{t.title}</span>
                <span className="block f-serif italic text-[#bdbcb5] mt-1">{t.version}</span>
              </span>
              <span className="hidden sm:block col-span-2 f-mono text-[11px] uppercase text-[var(--ash)]">{t.kind}</span>
              <span className="col-span-2 sm:col-span-1 text-right f-mono text-xs text-[var(--ash)]">{t.locked ? <Lock size={13} className="inline" /> : fmt(t.duration)}</span>
            </button>
            <SaveButton kind="track" slug={t.slug} label={false} className="p-2" />
          </li>
        );
      })}
    </ol>
    {list.length > limit && (
      <button onClick={() => setLimit((l) => l + 48)} className="mt-8 f-mono text-xs uppercase tracking-[0.2em] border border-[var(--bone)] px-6 py-3 hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors" data-testid="listen-show-more">Show more ({list.length - limit} left)</button>
    )}
    </div>
  );
}

function useTimings(slug) {
  const [lines, setLines] = useState([]);
  useEffect(() => {
    setLines([]);
    if (!slug) return undefined;
    let live = true;
    api.get(`/tracks/${slug}/timings`).then((r) => { if (live) setLines(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
    return () => { live = false; };
  }, [slug]);
  return lines;
}

function Lyrics() {
  const { current, tracks, time } = useAudio();
  const t = current || tracks[0];
  const [open, setOpen] = useState(true);
  const [follow, setFollow] = useState(true);
  const timings = useTimings(t?.audio ? t.slug : null);
  const box = useRef(null);
  const isCurrent = current?.slug === t?.slug && time > 0;
  let active = -1;
  if (isCurrent) for (const x of timings) { if (x.t <= time + 0.25) active = x.i; else break; }

  useEffect(() => {
    if (!follow || active < 0 || !box.current) return;
    const el = box.current.querySelector(`[data-line="${active}"]`);
    if (el) box.current.scrollTo({ top: el.offsetTop - box.current.clientHeight / 2.6, behavior: "smooth" });
  }, [active, follow]);

  if (!t?.lyrics) return null;
  const label = t.kind === "Spoken Reading" ? "Transcript" : "Lyrics";
  const synced = timings.length > 0;
  return (
    <section className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="listen-lyrics">
      <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
        <p className="eyebrow mb-4">{label}</p>
        <p className="f-display font-black uppercase text-4xl leading-none">{t.title}</p>
        <p className="f-serif italic text-[#bdbcb5] mt-2">{t.version}</p>
        <button onClick={() => setOpen((o) => !o)} className="mt-6 f-mono text-xs uppercase tracking-[0.2em] link-u" data-testid="listen-lyrics-toggle">{open ? "Fold" : "Unfold"} {label.toLowerCase()}</button>
        {synced && <button onClick={() => setFollow((f) => !f)} className="block mt-4 f-mono text-xs uppercase tracking-[0.2em] link-u w-fit" data-testid="listen-lyrics-follow">{follow ? "Stop following" : "Follow along"}</button>}
        {t.piece_slug && <Link to={`/explore/${t.piece_slug}`} className="block mt-4 f-mono text-xs uppercase tracking-[0.2em] link-u w-fit text-[var(--ash)]" data-testid="listen-read-essay-link">Read the essay</Link>}
      </div>
      {open && (
        <motion.div key={t.slug} ref={box} data-lenis-prevent initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="relative lg:col-span-8 f-serif text-xl sm:text-2xl leading-[1.6] text-[#d8d7d1] border-l border-[var(--line)] pl-6 sm:pl-10 max-h-[72vh] overflow-y-auto pr-2" data-testid="listen-lyrics-body">
          <p className="f-mono text-[11px] text-[var(--ash)] mb-6 normal-case">{synced ? "Lines light up as they're sung. " : ""}Tap any line to share it as a card.</p>
          {t.lyrics.split("\n").map((line, i) => <LyricLine key={i} line={line} track={t} i={i} state={!synced || !isCurrent ? "" : i === active ? "on" : "off"} />)}
        </motion.div>
      )}
    </section>
  );
}

function LyricLine({ line, track, i, state }) {
  const [busy, setBusy] = useState(false);
  const s = line.trim();
  if (!s) return <div className="h-6" />;
  if (/^\[.*\]$/.test(s)) return <p className="f-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ash)] mt-4 mb-1">{s.slice(1, -1)}</p>;
  if (s.length > 160) return <p data-line={i} className={`mb-4 transition-colors duration-500 ${state === "on" ? "text-[var(--bone)]" : state === "off" ? "text-[#8f8f89]" : ""}`}>{s}</p>;
  const share = async () => {
    setBusy(true);
    try {
      const how = await shareCard(
        { text: s, kicker: track.kind === "Spoken Reading" ? "From the reading" : "From the song", sub: `${track.title} \u2014 ${track.version}` },
        `outlaw-${track.slug}-${i}.png`,
        `\u201c${s}\u201d \u2014 ${track.title}, Outlaw Creative ${window.location.origin}/listen`,
      );
      if (how === "downloaded") toast("Card saved. Post it anywhere.");
    } catch (e) {
      if (e?.name !== "AbortError") toast("Could not make the card. Try again.");
    }
    setBusy(false);
  };
  return (
    <button data-line={i} onClick={share} disabled={busy} className={`group block text-left w-full origin-left transition-[color,transform] duration-500 hover:text-[var(--bone)] ${busy ? "text-[var(--signal)]" : state === "on" ? "text-[var(--bone)] translate-x-2" : state === "off" ? "text-[#8f8f89]" : ""}`} data-testid={`lyric-line-${i}`}>
      {state === "on" && <span className="absolute -ml-[2.05rem] sm:-ml-[2.85rem] mt-[0.7em] w-2 h-2 rounded-full bg-[var(--signal)]" />}
      {s}<Share2 size={13} className="inline ml-3 opacity-0 group-hover:opacity-60 transition-opacity align-middle" />
    </button>
  );
}

function AskCurrent() {
  const { current, tracks } = useAudio();
  const t = current || tracks[0];
  if (!t || t.locked) return null;
  return (
    <section className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-start-5 lg:col-span-8 space-y-10">
        <AskWork key={t.slug} trackSlug={t.slug} pieceSlug={t.piece_slug} title={t.title} id="listen-ask" />
        <LiveContext key={`ctx-${t.slug}`} path={`/context/track/${t.slug}`} title={`Where ${t.title} meets the world right now.`} />
      </div>
    </section>
  );
}

export default function Listen() {
  useSeo("Listen", "Songs, spoken readings and audio from Outlaw Creative. Sound & Light, The Angle Of The Voice and more.");
  return (
    <div className="px-5 sm:px-8 lg:px-12 pt-32 pb-24" data-testid="listen-page">
      <header className="mb-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <p className="eyebrow mb-5">02 / Listen</p>
          <h1 className="f-display font-black uppercase text-[20vw] sm:text-[14vw] lg:text-[10vw] leading-[0.82]">
            <MaskLines onMount lines={["The listening", <span key="r" className="outline-text">room</span>]} />
          </h1>
        </div>
        <p className="lg:col-span-4 self-end f-serif italic text-xl sm:text-2xl text-[#cfcec8]">Songs and spoken readings. Turn it up, or turn the lights down.</p>
      </header>
      <Deck />
      <FadeUp className="mt-24"><TrackList /></FadeUp>
      <Lyrics />
      <AskCurrent />
    </div>
  );
}
